import { request, requestList, buildQuery, orgBase } from '@/api/http'
import type { PlanEvent, PlanVersionState, SubmissionMarked } from '@/api/types/migration'
import type { AnnexAsset, CloseGate, ExportFormat, MigrationItem, MigrationItemStatus, MigrationPlan, MigrationPlanVersion, PlanChecks, RescanResult, SystemAnnex, SystemAgility, DiscoveryFinding } from '@/api/types/migration'

// ── Submission plan REST contract ────────────────────────────────────────────
// qtz-readiness serves the CSA submission plan under
// /organizations/{orgId}/migration/plan…: Part A, one Part B annex per CII
// system with one section-2 block per Discovery source, provenance per field,
// checks, versions, the submission record. Every call carries index_id. The
// Migration Tracker endpoints below are the contract the tracker pages will
// use once a backend exists; today they run on the browser store.

const base = (orgId: string) => `${orgBase(orgId)}/migration`
const q = (indexId: string, extra?: Record<string, unknown>) => buildQuery({ index_id: indexId, ...extra })

export interface RoadmapPayload {
  waves: Array<{ id: string; label: string; range: { from: string; to: string }; items: MigrationItem[] }>
  unscheduled: DiscoveryFinding[]
}

// Write bodies carry `actor` for the event log; the backend attributes a
// login's writes to the token's user regardless and keeps the body's actor
// only for service callers.
interface Actor { actor?: string }

// Partial updates: field name → value, plus `revert` (field names to reset to
// their synced value) and `actor`.
export type PlanPatch = Partial<Omit<MigrationPlan, 'id' | 'org_id' | 'index_id' | 'field_meta' | 'plan_state' | 'last_sync_at' | 'created_at' | 'updated_at'>> & { submission_date?: string | null; revert?: string[] } & Actor
export type AnnexPatch = Partial<Pick<SystemAnnex, 'system_name' | 'description' | 'function_supported' | 's3_text' | 'status'>> & { complete?: boolean; revert?: string[] } & Actor
export type BlockPatch = Partial<Pick<AnnexAsset, 'asset_model_version' | 'inventorisation_type' | 'service_provided' | 'protocols_algorithms' | 'package_type_vendor' | 'hosting' | 'will_migrate' | 'migrate_solution_timeline' | 'dependencies' | 'vendor_qs_roadmap' | 'notes'>> & { revert?: string[] } & Actor

export const migrationApi = {
  // Creates the plan on its first read, with the annexes of the CII systems.
  plan(orgId: string, indexId: string): Promise<MigrationPlan> {
    return request(`${base(orgId)}/plan${q(indexId)}`)
  },
  updatePlan(orgId: string, indexId: string, body: PlanPatch): Promise<MigrationPlan> {
    return request(`${base(orgId)}/plan${q(indexId)}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  // Refreshes every Discovery-sourced value and adds annexes for new CII systems.
  sync(orgId: string, indexId: string, body: Actor = {}): Promise<MigrationPlan> {
    return request(`${base(orgId)}/plan/sync${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  checks(orgId: string, indexId: string): Promise<PlanChecks> {
    return request(`${base(orgId)}/plan/checks${q(indexId)}`)
  },
  events(orgId: string, indexId: string): Promise<PlanEvent[]> {
    return requestList(`${base(orgId)}/plan/events${q(indexId)}`)
  },
  annexes(orgId: string, indexId: string): Promise<SystemAnnex[]> {
    return requestList(`${base(orgId)}/plan/annexes${q(indexId)}`)
  },
  annex(orgId: string, indexId: string, id: string): Promise<SystemAnnex> {
    return request(`${base(orgId)}/plan/annexes/${id}${q(indexId)}`)
  },
  createAnnex(orgId: string, indexId: string, body: { system_id: string | null; duplicate_of?: string } & Actor): Promise<SystemAnnex> {
    return request(`${base(orgId)}/plan/annexes${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  updateAnnex(orgId: string, indexId: string, id: string, body: AnnexPatch): Promise<SystemAnnex> {
    return request(`${base(orgId)}/plan/annexes/${id}${q(indexId)}`, { method: 'PATCH', body: JSON.stringify(body) })
  },
  deleteAnnex(orgId: string, indexId: string, id: string, body: Actor = {}): Promise<void> {
    return request(`${base(orgId)}/plan/annexes/${id}${q(indexId)}`, { method: 'DELETE', body: JSON.stringify(body) })
  },
  // Takes the annex out of Part B with a reason; syncs leave it archived.
  archiveAnnex(orgId: string, indexId: string, id: string, body: { reason: string } & Actor): Promise<SystemAnnex> {
    return request(`${base(orgId)}/plan/annexes/${id}/archive${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  restoreAnnex(orgId: string, indexId: string, id: string, body: Actor = {}): Promise<SystemAnnex> {
    return request(`${base(orgId)}/plan/annexes/${id}/restore${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  reorderAnnexes(orgId: string, indexId: string, body: { ordered_ids: string[] } & Actor): Promise<SystemAnnex[]> {
    return requestList(`${base(orgId)}/plan/annexes/reorder${q(indexId)}`, { method: 'PATCH', body: JSON.stringify(body) })
  },
  // From a Discovery source, or a manual row when source_id is null.
  addAnnexAsset(orgId: string, indexId: string, annexId: string, body: { source_id: string | null } & Actor): Promise<AnnexAsset> {
    return request(`${base(orgId)}/plan/annexes/${annexId}/assets${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  updateAnnexAsset(orgId: string, indexId: string, annexId: string, assetId: string, body: BlockPatch): Promise<AnnexAsset> {
    return request(`${base(orgId)}/plan/annexes/${annexId}/assets/${assetId}${q(indexId)}`, { method: 'PATCH', body: JSON.stringify(body) })
  },
  deleteAnnexAsset(orgId: string, indexId: string, annexId: string, assetId: string, body: Actor = {}): Promise<void> {
    return request(`${base(orgId)}/plan/annexes/${annexId}/assets/${assetId}${q(indexId)}`, { method: 'DELETE', body: JSON.stringify(body) })
  },
  // A save that fails a blocking check is refused with 409 BLOCKING_CHECKS;
  // the ApiError's data lists the codes under `blocking` (§11.2).
  saveVersion(orgId: string, indexId: string, body: { label: string; submission_date: string; note?: string | null; export: ExportFormat[] } & Actor): Promise<MigrationPlanVersion> {
    return request(`${base(orgId)}/plan/versions${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  versions(orgId: string, indexId: string): Promise<MigrationPlanVersion[]> {
    return requestList(`${base(orgId)}/plan/versions${q(indexId)}`)
  },
  submissions(orgId: string, indexId: string): Promise<SubmissionMarked[]> {
    return requestList(`${base(orgId)}/plan/submissions${q(indexId)}`)
  },
  // Iteration 3 §7: records submission_marked and freezes v{n}. The backend
  // names the signed-in user and records the IP; reauth and auth_time are what
  // the portal reports about its step-up prompt.
  markSubmitted(orgId: string, indexId: string, body: Omit<SubmissionMarked, 'id' | 'version' | 'recorded_at' | 'ip' | 'final_pdf_version_id'> & { auth_time: string } & Actor): Promise<SubmissionMarked> {
    return request(`${base(orgId)}/plan/submit${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  startNewVersion(orgId: string, indexId: string, body: Actor = {}): Promise<PlanVersionState> {
    return request(`${base(orgId)}/plan/versions/next${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  // Administrator only (403 ADMIN_REQUIRED otherwise); not offered in the UI.
  reopenSubmission(orgId: string, indexId: string, body: { reason: string } & Actor): Promise<PlanVersionState> {
    return request(`${base(orgId)}/plan/reopen${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },

  // ── Migration Tracker (no backend yet; the pages run on the browser store) ──
  items(orgId: string, indexId: string, params?: { status?: MigrationItemStatus; system?: string; wave?: string; owner?: string }): Promise<MigrationItem[]> {
    return requestList(`${base(orgId)}/items${q(indexId, params)}`)
  },
  createItem(orgId: string, indexId: string, body: Partial<MigrationItem> & { asset_ids: string[] }): Promise<MigrationItem> {
    return request(`${base(orgId)}/items${q(indexId)}`, { method: 'POST', body: JSON.stringify(body) })
  },
  updateItem(orgId: string, indexId: string, id: string, body: Partial<MigrationItem> & { asset_ids?: string[] }): Promise<MigrationItem> {
    return request(`${base(orgId)}/items/${id}${q(indexId)}`, { method: 'PATCH', body: JSON.stringify(body) })
  },
  rescan(orgId: string, indexId: string, id: string): Promise<RescanResult> {
    return request(`${base(orgId)}/items/${id}/rescan${q(indexId)}`, { method: 'POST' })
  },
  // Runs can_close server-side; a failing gate returns the asset ids still vulnerable.
  close(orgId: string, indexId: string, id: string): Promise<MigrationItem | CloseGate> {
    return request(`${base(orgId)}/items/${id}/close${q(indexId)}`, { method: 'POST' })
  },
  agility(orgId: string, indexId: string, systemId: string, body: Partial<SystemAgility>): Promise<SystemAgility> {
    return request(`${base(orgId)}/systems/${systemId}/agility${q(indexId)}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  roadmap(orgId: string, indexId: string): Promise<RoadmapPayload> {
    return request(`${base(orgId)}/roadmap${q(indexId)}`)
  },
}
